// The model provider and the integrations, each a Connections page of its own
// (#805, on #795's navigation decision). Both were panels on workspace settings
// (#804's General); the panels are unchanged, and what each may do is still read
// from the roles this workspace answers to.

import { readingAsOrg, type OrgKind } from "../api/memberships";
import type { AdapterKind, CreatedWebhook, DeliveryRow, IntegrationPatch, IntegrationRow, NewIntegration, RotatedWebhook, WebhookConfig } from "../api/integrations";
import type { StepUp } from "../api/policy";
import type { BeginPasskey, StepUpFactors } from "./StepUp";
import type { JiraLister } from "../api/jiraLists";
import type { SlackLister } from "../api/slackChannels";
import type { SlackInstallResult } from "../api/slackInstall";
import { roleHolds, type Role } from "../api/roles";
import { isPlatformViewer, onOwnMembership, type SessionResponse } from "../api/session";
import type { ProviderCheck, ProviderRefusal, ProviderRow, Setup } from "../api/sources";
import { Empty, PageIntro } from "./chrome";
import { Integrations } from "./Integrations";
import { Provider } from "./Provider";

/** What an organisation reads at a page whose connection is each workspace's own. */
function InAWorkspace({
  destination,
  title,
  orgKind,
}: {
  destination: "provider" | "integrations";
  title: string;
  orgKind: OrgKind | null;
}) {
  return (
    <>
      <PageIntro destination={destination} />
      <section className="panel">
        <Empty title={title}>
          <p>{readingAsOrg(orgKind, "see its connections")}</p>
        </Empty>
      </section>
    </>
  );
}

export function ProviderPage({
  session,
  orgKind = null,
  roles,
  rolesError,
  provider,
  providerRefusal = null,
  providerError,
  setup,
  onSetProvider,
  onCheckProvider,
}: {
  session: SessionResponse;
  roles: Role[] | null;
  rolesError: string | null;
  /** The model provider (#422): null for none set, undefined while unread. */
  provider: ProviderRow | null | undefined;
  /** When that provider last refused an investigation, or null (#1415). */
  providerRefusal?: ProviderRefusal | null;
  providerError: string | null;
  /** What is left before the first investigation, or null while unread (#805). */
  setup: Setup | null;
  onSetProvider: (body: {
    provider: "openai" | "litellm";
    api_base: string;
    api_key: string;
    high_model_id: string;
    low_model_id: string | null;
  }) => Promise<ProviderRow>;
  onCheckProvider: () => Promise<ProviderCheck>;
  /** The kind of the org this context reads under, for the org-context empty state (#1205). */
  orgKind?: OrgKind | null;
}) {
  if (session.workspace_id === null)
    return <InAWorkspace destination="provider" title="The model provider belongs to a workspace" orgKind={orgKind} />;
  const permits = (permission: string) => rolesError === null && roleHolds(roles, session.role, permission);
  return (
    <>
      <PageIntro destination="provider" />
      <Provider
        provider={provider}
        lastRefusal={providerRefusal}
        setup={setup}
        canEdit={onOwnMembership(session) && permits("settings:write")}
        canCheck={session.impersonated_by === null && !isPlatformViewer(session) && permits("settings:read")}
        error={providerError}
        onSet={onSetProvider}
        onCheck={onCheckProvider}
      />
    </>
  );
}

export function IntegrationsPage({
  session,
  orgKind = null,
  roles,
  rolesError,
  integrations,
  integrationsError,
  connect,
  slackInstall = null,
  deliveries,
  onCreate,
  onDisable,
  onEnable,
  onDelete,
  onUpdate,
  onExpand,
  onRetry,
  onTest,
  onAwaitCheck,
  onSendTest,
  onSendTestEvent,
  onListJira,
  onListSlack,
  onRegisterWebhook,
  onRotateWebhook,
  stepUpFactors,
  beginPasskey,
}: {
  session: SessionResponse;
  roles: Role[] | null;
  rolesError: string | null;
  /** The workspace's Jira and Slack connections, or null while unread (#492). */
  integrations: IntegrationRow[] | null;
  integrationsError: string | null;
  /** The connecting flow this location asked for (#602), or null. */
  connect: AdapterKind | null;
  /** What Add to Slack's callback said, when this location is the return from it (#937). */
  slackInstall?: SlackInstallResult | null;
  /** Each expanded connection's recent deliveries, by id (#579). */
  deliveries?: Record<string, DeliveryRow[] | null | string> | null;
  /** Absent, the panel is not drawn: a console that cannot connect offers nothing. */
  onCreate?: (body: NewIntegration) => Promise<IntegrationRow | void>;
  onDisable?: (id: string) => Promise<void>;
  onEnable?: (id: string) => Promise<IntegrationRow | null>;
  onDelete?: (id: string) => Promise<void>;
  onUpdate?: (id: string, body: IntegrationPatch) => Promise<IntegrationRow | void>;
  onExpand?: (id: string) => Promise<void>;
  onRetry?: (connectionId: string, deliveryId: string) => Promise<void>;
  onTest?: (id: string) => Promise<IntegrationRow | null>;
  onAwaitCheck?: (id: string) => Promise<IntegrationRow | null>;
  onSendTest?: (id: string) => Promise<void>;
  /** Send a webhook receiver its test event (#1076): the row carrying its answer, or null. */
  onSendTestEvent?: (id: string) => Promise<IntegrationRow | null>;
  onListJira?: JiraLister;
  onListSlack?: SlackLister;
  /** Register a webhook receiver under a step-up (#1073): the answer carrying its secret, once. */
  onRegisterWebhook?: (config: WebhookConfig, stepUp: StepUp) => Promise<CreatedWebhook>;
  /** Rotate a receiver's secret under a step-up (#1075): the answer carrying the new one, once. */
  onRotateWebhook?: (id: string, stepUp: StepUp) => Promise<RotatedWebhook>;
  stepUpFactors?: () => Promise<StepUpFactors>;
  beginPasskey?: BeginPasskey;
  /** The kind of the org this context reads under, for the org-context empty state (#1205). */
  orgKind?: OrgKind | null;
}) {
  if (session.workspace_id === null)
    return <InAWorkspace destination="integrations" title="Integrations belong to a workspace" orgKind={orgKind} />;
  return (
    <>
      <PageIntro destination="integrations" />
      {onCreate === undefined || onDisable === undefined ? null : <Integrations
        integrations={integrations}
        deliveries={deliveries}
        error={integrationsError}
        connect={connect}
        slackInstall={slackInstall}
        mayManage={onOwnMembership(session) && rolesError === null && roleHolds(roles, session.role, "integrations:manage")}
        onCreate={onCreate}
        onDisable={onDisable}
        onEnable={onEnable}
        onDelete={onDelete}
        onUpdate={onUpdate}
        onExpand={onExpand}
        onRetry={onRetry}
        onTest={onTest}
        onAwaitCheck={onAwaitCheck}
        onSendTest={onSendTest}
        onSendTestEvent={onSendTestEvent}
        onListJira={onListJira}
        onListSlack={onListSlack}
        onRegisterWebhook={onRegisterWebhook}
        onRotateWebhook={onRotateWebhook}
        stepUpFactors={stepUpFactors}
        beginPasskey={beginPasskey}
      />}
    </>
  );
}
